import * as lamejs from '@breezystack/lamejs'

const SAMPLE_BLOCK_SIZE = 1152
const MP3_BITRATE = 128

const float32ToInt16 = (float32Array) => {
  const int16 = new Int16Array(float32Array.length)
  for (let i = 0; i < float32Array.length; i++) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]))
    int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
  }
  return int16
}

const encodeAudioBufferToMp3 = (audioBuffer) => {
  const channels = audioBuffer.numberOfChannels
  const mp3encoder = new lamejs.Mp3Encoder(channels === 1 ? 1 : 2, audioBuffer.sampleRate, MP3_BITRATE)
  const mp3Data = []
  const length = audioBuffer.length

  const pushEncoded = (mp3buf) => {
    if (mp3buf?.length > 0) mp3Data.push(mp3buf)
  }

  if (channels === 1) {
    const samples = float32ToInt16(audioBuffer.getChannelData(0))
    for (let i = 0; i < samples.length; i += SAMPLE_BLOCK_SIZE) {
      pushEncoded(mp3encoder.encodeBuffer(samples.subarray(i, i + SAMPLE_BLOCK_SIZE)))
    }
  } else {
    const left = float32ToInt16(audioBuffer.getChannelData(0))
    const right = float32ToInt16(audioBuffer.getChannelData(channels > 1 ? 1 : 0))
    for (let i = 0; i < length; i += SAMPLE_BLOCK_SIZE) {
      pushEncoded(mp3encoder.encodeBuffer(
        left.subarray(i, i + SAMPLE_BLOCK_SIZE),
        right.subarray(i, i + SAMPLE_BLOCK_SIZE),
      ))
    }
  }

  pushEncoded(mp3encoder.flush())
  return new Blob(mp3Data, { type: 'audio/mp3' })
}

export const audioBufferToWav = (buffer) => {
  const length = buffer.length
  const numberOfChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const bytesPerSample = 2
  const blockAlign = numberOfChannels * bytesPerSample
  const byteRate = sampleRate * blockAlign
  const dataSize = length * blockAlign
  const bufferSize = 44 + dataSize
  const arrayBuffer = new ArrayBuffer(bufferSize)
  const view = new DataView(arrayBuffer)

  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i))
    }
  }

  writeString(0, 'RIFF')
  view.setUint32(4, bufferSize - 8, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, numberOfChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bytesPerSample * 8, true)
  writeString(36, 'data')
  view.setUint32(40, dataSize, true)

  let offset = 44
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]))
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
      offset += 2
    }
  }

  return arrayBuffer
}

export const convertWavToMp3 = async (wavBlob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result
        const dataView = new DataView(arrayBuffer)
        const wav = lamejs.WavHeader.readHeader(dataView)
        const samples = new Int16Array(arrayBuffer, wav.dataOffset, wav.dataLen / 2)
        const mp3encoder = new lamejs.Mp3Encoder(wav.channels, wav.sampleRate, MP3_BITRATE)
        const mp3Data = []

        const pushEncoded = (mp3buf) => {
          if (mp3buf?.length > 0) mp3Data.push(mp3buf)
        }

        if (wav.channels === 1) {
          for (let i = 0; i < samples.length; i += SAMPLE_BLOCK_SIZE) {
            pushEncoded(mp3encoder.encodeBuffer(samples.subarray(i, i + SAMPLE_BLOCK_SIZE)))
          }
        } else {
          const sampleCount = samples.length / wav.channels
          const left = new Int16Array(sampleCount)
          const right = new Int16Array(sampleCount)
          for (let i = 0; i < sampleCount; i++) {
            left[i] = samples[i * wav.channels]
            right[i] = samples[i * wav.channels + 1]
          }
          for (let i = 0; i < sampleCount; i += SAMPLE_BLOCK_SIZE) {
            pushEncoded(mp3encoder.encodeBuffer(
              left.subarray(i, i + SAMPLE_BLOCK_SIZE),
              right.subarray(i, i + SAMPLE_BLOCK_SIZE),
            ))
          }
        }

        pushEncoded(mp3encoder.flush())
        resolve(new Blob(mp3Data, { type: 'audio/mp3' }))
      } catch (error) {
        reject(error)
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(wavBlob)
  })

const CONVERT_TO_MP3_EXTENSIONS = new Set(['m4a', 'mp4'])

export const convertMediaFileToMp3 = async (file) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)()
  try {
    const arrayBuffer = await file.arrayBuffer()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const mp3Blob = encodeAudioBufferToMp3(audioBuffer)
    const baseName = file.name.replace(/\.[^/.]+$/, '') || 'audio'

    return new File([mp3Blob], `${baseName}.mp3`, { type: 'audio/mp3' })
  } finally {
    await audioContext.close?.()
  }
}

export const prepareCampaignAudioUpload = async (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase()
  if (CONVERT_TO_MP3_EXTENSIONS.has(extension)) {
    return convertMediaFileToMp3(file)
  }
  return file
}
